import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";
import {
  Upload,
  Send,
  Loader2,
  Image as ImgIcon,
  Mic,
  Film,
  FileText,
  ArrowLeft,
  Printer,
  Target,
  ExternalLink,
  CheckCircle2,
  Lock
} from "lucide-react";
import PetCompanion from "../../components/shared/PetCompanion";
import QuestBanner from "../../components/shared/QuestBanner";
import QuestQuiz from "../../components/shared/QuestQuiz";
import TeachStep from "../../components/shared/QuestTeach";
import QuestVisual from "../../components/shared/QuestVisual";
import PrintableResource from "../../components/shared/PrintableResource";
import { openPaperLesson } from "../../components/shared/lessonPrint";
import SpellingSegment from "../../components/shared/SpellingSegment";
import {
  RevealCards,
  SortActivity,
  ChoiceSet,
  SentenceBuilder,
  Planner
} from "../../components/shared/QuestActivities";
import { Leaf } from "../../components/shared/Botanical";

const cleanStepTitle = (title = "") =>
  title.replace(/^(\S+\s)?Step \d+:\s*/, "$1");

// Parent-only paragraphs (starting with "Parent:" or "Grown-up:") must not be shown to the child.
const childText = (text = "") =>
  String(text || "")
    .split(/\n\s*\n/)
    .filter((para) => !/^\s*(Parent|Grown-up|Grown up)\s*(note|check)?\s*:/i.test(para))
    .join("\n\n")
    .trim();

const hasScoredQuiz = (lesson) =>
  (lesson?.quiz || []).some((q) => q.type !== "short_answer");

const CONTINUE_STYLE = { backgroundColor: "#1F3B2D", color: "#F5EFE0" };

const progressKey = (aid) => `sidequest-progress-${aid}`;

const lessonHoardWords = (lesson) => {
  const fromSpelling = (lesson?.spelling?.words || []).map((w) => w.word);
  const extra = lesson?.hoard_words || [];
  return [...new Set([...fromSpelling, ...extra].filter(Boolean))];
};

const shuffled = (arr, isBad) => {
  let out = arr;

  for (let attempt = 0; attempt < 30; attempt += 1) {
    out = [...arr];

    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }

    const same = out.every((item, i) => item === arr[i]);

    if (!same && !(isBad && isBad(out))) return out;
  }

  return out;
};

const sortIsPredictable = (items, bucketCount) =>
  items.every((item, i) => item.answer === i % bucketCount);

const shuffleQuestion = (q) => {
  const order = shuffled(q.options.map((_, i) => i));

  return {
    ...q,
    options: order.map((i) => q.options[i]),
    correct_index:
      typeof q.correct_index === "number"
        ? order.indexOf(q.correct_index)
        : q.correct_index
  };
};

export default function ChildLesson() {
  const { aid } = useParams();
  const nav = useNavigate();

  const [a, setA] = useState(null);
  const [response, setResponse] = useState("");
  const [promptAnswer, setPromptAnswer] = useState("");
  const [reflection, setReflection] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [needsHelp, setNeedsHelp] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [quizResult, setQuizResult] = useState(null);
  const [quizShortAnswers, setQuizShortAnswers] = useState({});
  const [plan, setPlan] = useState({});
  const [done, setDone] = useState({});
  const [skipped, setSkipped] = useState({});
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState(0);
  const [sortActivity, setSortActivity] = useState(null);
  const [wordQsMixed, setWordQsMixed] = useState(null);
  const [restored, setRestored] = useState(false);

  const [activeChallenge, setActiveChallenge] = useState(null);
  const [challengeResponse, setChallengeResponse] = useState("");
  const [challengeFiles, setChallengeFiles] = useState([]);

  const fileRef = useRef();
  const chFileRef = useRef();

  useEffect(() => {
    api.get(`/assignments/${aid}`).then((result) => {
      const lesson = result.data.lesson || {};

      if (lesson.sort_activity?.items?.length) {
        const bucketCount = (lesson.sort_activity.buckets || []).length || 3;

        setSortActivity({
          ...lesson.sort_activity,
          items: shuffled(lesson.sort_activity.items, (list) =>
            sortIsPredictable(list, bucketCount)
          )
        });
      }

      if (lesson.word_challenges?.length) {
        setWordQsMixed(shuffled(lesson.word_challenges).map(shuffleQuestion));
      }

      try {
        const raw = window.localStorage.getItem(progressKey(aid));
        // Prefer this device's latest progress; otherwise restore the server hand-in
        // snapshot (including when a parent returned work on another device).
        const saved = raw ? JSON.parse(raw) : result.data.draft;

        if (saved) {

          setDone(saved.done || {});
          setSkipped(saved.skipped || {});
          setCurrent(saved.current || 0);
          setOpen(typeof saved.open === "number" ? saved.open : 0);
          setPlan(saved.plan || {});
          setResponse(saved.response || "");
          setPromptAnswer(saved.promptAnswer || "");
          setReflection(saved.reflection || "");
          setFiles(saved.files || []);
          setNeedsHelp(!!saved.needsHelp);
          setQuizResult(saved.quizResult || null);
          setQuizShortAnswers(saved.quizShortAnswers || {});
        }
      } catch {
        // ignore unreadable saved progress
      }

      setA(result.data);
      setRestored(true);

      if (result.data.status === "not_started") {
        api.put(`/assignments/${aid}/status?status=opened`).catch(() => {});
      }

      // Earlier submissions do not mean a returned lesson is still locked.
      // Its current assignment status is the source of truth.
      setSubmitted(["submitted", "awaiting_help", "accepted", "demonstrated", "completed"].includes(result.data.status));

      const hoardWords = lessonHoardWords(lesson);

      if (hoardWords.length > 0) {
        api
          .post("/word-bank/words", {
            words: hoardWords,
            source: "lesson",
            lesson_id: lesson.id
          })
          .then((res) => {
            const count = res.data?.added?.length || 0;

            if (count > 0) {
              toast.success(
                `${count} new word${count === 1 ? "" : "s"} added to your Word Hoard 🐾`
              );
            }
          })
          .catch(() => {});
      }
    });
  }, [aid]);

  useEffect(() => {
    if (!restored) return;

    try {
      window.localStorage.setItem(
        progressKey(aid),
        JSON.stringify({
          done,
          skipped,
          current,
          open,
          plan,
          response,
          promptAnswer,
          reflection,
          files,
          needsHelp,
          quizResult,
          quizShortAnswers
        })
      );
    } catch {
      // storage full or blocked; progress just will not be saved
    }
  }, [
    restored,
    aid,
    done,
    skipped,
    current,
    open,
    plan,
    response,
    promptAnswer,
    reflection,
    files,
    needsHelp,
    quizResult,
    quizShortAnswers
  ]);

  const mark = (key) => (value) =>
    setDone((d) => (d[key] === value ? d : { ...d, [key]: value }));

  const uploadFile = async (file, setter) => {
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("context", "submission");

    try {
      const { data } = await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setter((current) => [...current, data]);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const quizRequired = hasScoredQuiz(a?.lesson);
  const quizPassed = !quizRequired || !!quizResult?.passed;

  const submit = async () => {
    const lesson = a.lesson || {};

    if (!quizPassed) {
      toast.error("Clear the quiz (90% or more) before submitting");
      return;
    }

    if (!response.trim() && files.length === 0) {
      toast.error("Add your finished work or upload a photo of it");
      return;
    }

    setSubmitting(true);

    const quizLine = quizResult
      ? `[Quiz: ${quizResult.score}/${quizResult.total} (${quizResult.percent}%), attempt ${quizResult.attempt}, PASSED]\n`
      : "";

    const shortAnswerLine = (lesson.quiz || []).map((question, index) =>
      question.type === "short_answer" ? `\n[Quiz question ${index + 1}: ${question.question}] ${(quizShortAnswers[index] || "").trim()}` : ""
    ).join("");

    const planLine = (lesson.planner_fields || []).length
      ? `[Plan] ${lesson.planner_fields
          .map((f) => `${f.key}: ${(plan[f.key] || "").trim()}`)
          .join(" | ")}\n`
      : "";

    const answerLine =
      promptAnswer.trim() && lesson.response_prompt
        ? `\n\n[Answer to "${lesson.response_prompt}"] ${promptAnswer.trim()}`
        : "";

    try {
      await api.post("/submissions", {
        assignment_id: aid,
        response_text: `${quizLine}${planLine}${response}${answerLine}${shortAnswerLine}`,
        reflection,
        file_ids: files.map((file) => file.id),
        needs_help: needsHelp,
        // Carry the full editable state with hand-in so returned work can be restored.
        draft: { done, skipped, current, open, plan, response, promptAnswer, reflection, files, needsHelp, quizResult, quizShortAnswers }
      });

      toast.success(
        needsHelp
          ? "Sent for help"
          : "Submitted! Try a follow-up challenge 🌱"
      );

      if (lessonHoardWords(lesson).length > 0) {
        toast.info("Don't forget to practise your new words in the Word Hoard 🐾");
      }

      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitChallenge = async () => {
    if (!challengeResponse.trim() && challengeFiles.length === 0) {
      toast.error("Show your work");
      return;
    }

    try {
      await api.post("/submissions", {
        assignment_id: aid,
        response_text: `[Challenge: ${activeChallenge.title}] ${challengeResponse}`,
        file_ids: challengeFiles.map((file) => file.id),
        needs_help: false
      });

      toast.success(`Challenge complete! ${activeChallenge.title} ✨`);
      setActiveChallenge(null);
      setChallengeResponse("");
      setChallengeFiles([]);
    } catch {
      toast.error("Failed");
    }
  };

  const printLesson = () => {
    if (!openPaperLesson(a.lesson)) toast.error("Allow pop-ups for this site to open the printable lesson");
  };

  if (!a) {
    return <div className="text-stone-500">Loading…</div>;
  }

  const l = a.lesson || {};

  const embeds = (l.resources || []).filter(
    (r) => r.type === "video" && r.embed_url
  );
  const links = (l.resources || []).filter(
    (r) => !(r.type === "video" && r.embed_url)
  );
  const flip = (l.interactive_activities || []).find(
    (x) => x.type === "flip_cards"
  );
  const teachSteps = l.teach_steps || [];
  const wordQs = wordQsMixed || l.word_challenges || [];
  const sortData = sortActivity || l.sort_activity;
  const builder = l.suspense_builder;
  const plannerFields = l.planner_fields || [];
  const spellingData = l.spelling && (l.spelling.teaching || (l.spelling.words || []).length) ? l.spelling : null;
  const hasHoardWords = lessonHoardWords(l).length > 0;
  const guidedText = childText(l.guided_practice);
  const taskText = childText(l.independent_task);
  const plannerTitle = l.planner_title || "Plan your work";
  const plannerIntro =
    l.planner_intro || "Good writers plan first. Fill in each box with a few words.";

  const stages = [
    { key: "accept", icon: "📜", title: "Accept the quest" },
    ...teachSteps.map((step, i) => ({
      key: `teach${i}`,
      icon: step.icon || "📖",
      title: `Lesson ${i + 1}: ${step.title}`
    })),
    {
      key: "learn",
      icon: "🧭",
      title: teachSteps.length > 0 ? "Put it all together" : "Learn the map"
    },
    spellingData && {
      key: "spelling",
      icon: "🔤",
      title: spellingData.focus ? `Spelling: ${spellingData.focus}` : "Spelling"
    },
    sortData && { key: "sort", icon: "🧩", title: "Spot the structure" },
    embeds.length > 0 && { key: "watch", icon: "🎬", title: "Watch and notice" },
    (wordQs.length > 0 || builder) && {
      key: "words",
      icon: "💎",
      title: "Word power"
    },
    guidedText && { key: "guided", icon: "🤝", title: "Try it with help" },
    plannerFields.length > 0 && {
      key: "plan",
      icon: "🗺️",
      title: plannerTitle
    },
    { key: "write", icon: "✍️", title: "Do the task" },
    quizRequired && { key: "quiz", icon: "🛡️", title: "Quest check" },
    { key: "submit", icon: "🏆", title: "Hand it in" }
  ].filter(Boolean);

  const planOk = plannerFields.every(
    (f) => (plan[f.key] || "").trim().length >= 3
  );

  const stageDone = (key) => {
    if (skipped[key]) return true;

    switch (key) {
      case "accept":
        return !!done.accept;
      case "learn":
        return !!done.learn;
      case "spelling":
        return !!done.spelling;
      case "sort":
        return !!done.sort;
      case "guided":
        return !!done.guided;
      case "watch":
        return embeds.every((_, i) => done[`watch${i}`]);
      case "words":
        return (
          (wordQs.length === 0 || !!done.wordsA) &&
          (!builder || !!done.wordsB)
        );
      case "plan":
        return planOk;
      case "write":
        return response.trim().length > 0 || files.length > 0;
      case "quiz":
        return quizPassed;
      case "submit":
        return submitted;
      default:
        return !!done[key];
    }
  };

  const advance = (from) => {
    const next = Math.min(from + 1, stages.length - 1);
    setCurrent((c) => Math.max(c, next));
    setOpen(next);
  };

  const skipStage = (key) => {
    if (
      window.confirm(
        "Grown-up check: skip this activity? Only do this if it is not working."
      )
    ) {
      setSkipped((s) => ({ ...s, [key]: true }));
    }
  };

  const completed = stages.filter((s) => stageDone(s.key)).length;
  const percent = Math.round((completed / stages.length) * 100);

  const supportBanner =
    a.support_level === "red"
      ? {
          cls: "bg-rose-50 border-rose-200 text-rose-900",
          text: "Red zone — wait for a grown-up before starting"
        }
      : a.support_level === "yellow"
        ? {
            cls: "bg-amber-50 border-amber-200 text-amber-900",
            text: "Yellow — try it, then ask for help if you get stuck"
          }
        : {
            cls: "bg-emerald-50 border-emerald-200 text-emerald-900",
            text: "Green — have a go on your own"
          };

  const renderBody = (key) => {
    if (key.startsWith("teach")) {
      const index = Number(key.replace("teach", ""));

      return (
        <TeachStep step={teachSteps[index]} onComplete={mark(key)} />
      );
    }

    switch (key) {
      case "accept":
        return (
          <div>
            <p
              className="font-display text-xl font-bold"
              style={{ color: "#1F3B2D" }}
            >
              {l.child_mission || l.learning_intention}
            </p>

            <p className="text-sm mt-3 text-stone-700">
              Don't worry if this is new. You will learn each idea one small
              step at a time, with an example and a quick try before moving on.
            </p>

            {(l.success_criteria || []).length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-mono uppercase text-stone-500">
                  By the end you will be able to
                </div>

                <ul className="list-disc pl-5 mt-1 space-y-1 text-sm">
                  {l.success_criteria.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-sm mt-4">
              <strong>You will need:</strong>{" "}
              {(l.materials || []).join(", ") || "Nothing special"}
            </p>

            {!done.accept ? (
              <button
                type="button"
                onClick={() => {
                  mark("accept")(true);
                  advance(0);
                }}
                className="mt-5 rounded-full px-6 py-3 text-base font-bold"
                style={{ backgroundColor: "#C77B5B", color: "#F5EFE0" }}
                data-testid="accept-quest"
              >
                ⚔️ I accept this quest!
              </button>
            ) : (
              <p className="mt-4 text-sm font-bold text-green-800">
                Quest accepted. Good luck, explorer!
              </p>
            )}
          </div>
        );

      case "learn":
        return (
          <div className="space-y-4">
            {teachSteps.length === 0 && (
              <div className="whitespace-pre-wrap text-sm">
                {l.explicit_teaching}
              </div>
            )}

            {l.worked_example && (
              <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-4 text-sm">
                <div className="font-bold mb-1">
                  {teachSteps.length > 0
                    ? "Here is a complete worked example. Read it slowly."
                    : "Read this example"}
                </div>

                {(l.worked_visuals || []).map((v, i) => (
                  <QuestVisual key={i} visual={v} />
                ))}

                <div className="whitespace-pre-wrap leading-relaxed">
                  {l.worked_example}
                </div>
              </div>
            )}

            {flip && (
              <div>
                <div className="font-bold text-sm mb-2">{flip.title}</div>
                <RevealCards
                  cards={flip.cards || []}
                  onComplete={mark("learn")}
                />
              </div>
            )}

            {!flip && (
              <button
                type="button"
                onClick={mark("learn")}
                className="rounded-full px-5 py-2 text-sm font-bold"
                style={CONTINUE_STYLE}
              >
                I have read it
              </button>
            )}
          </div>
        );

      case "spelling":
        return (
          <SpellingSegment
            spelling={spellingData}
            lessonId={l.id}
            onComplete={mark("spelling")}
          />
        );

      case "sort":
        return <SortActivity activity={sortData} onComplete={mark("sort")} />;

      case "guided":
        return (
          <div className="space-y-4">
            <div className="rounded-xl bg-indigo-50 border border-indigo-200 p-4 text-sm whitespace-pre-wrap leading-relaxed">
              {guidedText}
            </div>

            {!done.guided && (
              <button
                type="button"
                onClick={mark("guided")}
                className="rounded-full px-5 py-2 text-sm font-bold"
                style={CONTINUE_STYLE}
              >
                I have read this and I am ready
              </button>
            )}
          </div>
        );

      case "watch":
        return (
          <div className="space-y-5">
            {embeds.map((resource, i) => (
              <div
                key={i}
                className="rounded-xl border p-3 bg-white"
                style={{ borderColor: "#D4C8A8" }}
              >
                <div className="font-semibold text-sm mb-2">
                  🎬 {resource.title}
                </div>

                <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
                  <iframe
                    src={resource.embed_url}
                    title={resource.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>

                {resource.url && (
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold"
                    style={{ color: "#4A5D3A" }}
                  >
                    <ExternalLink size={12} />
                    Video not playing? Open it with a grown-up
                  </a>
                )}

                {resource.prompt && (
                  <p className="text-xs text-stone-700 mt-2">{resource.prompt}</p>
                )}

                <div className="mt-3">
                  {resource.check ? (
                    <ChoiceSet
                      questions={[resource.check]}
                      onComplete={mark(`watch${i}`)}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => mark(`watch${i}`)(true)}
                      className="rounded-full px-5 py-2 text-sm font-bold"
                      style={CONTINUE_STYLE}
                    >
                      I watched it
                    </button>
                  )}
                </div>
              </div>
            ))}

            {links.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-mono uppercase text-stone-500">
                  Optional extras (with a grown-up)
                </div>

                {links.map((resource, i) => (
                  <a
                    key={i}
                    href={resource.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-xl p-3 border bg-white hover:bg-stone-50 text-sm"
                    style={{ borderColor: "#D4C8A8" }}
                  >
                    📖 {resource.title}
                  </a>
                ))}
              </div>
            )}
          </div>
        );

      case "words":
        return (
          <div className="space-y-5">
            {wordQs.length > 0 && (
              <div>
                <p className="text-sm text-stone-700 mb-3">
                  Pick the best word for each sentence.
                </p>
                <ChoiceSet questions={wordQs} onComplete={mark("wordsA")} />
              </div>
            )}

            {builder && (
              <SentenceBuilder builder={builder} onComplete={mark("wordsB")} />
            )}
          </div>
        );

      case "plan":
        return (
          <div>
            <p className="text-sm text-stone-700 mb-3">{plannerIntro}</p>

            <Planner
              fields={plannerFields}
              values={plan}
              setValues={setPlan}
              onComplete={() => {}}
            />
          </div>
        );

      case "write":
        return (
          <div className="space-y-4">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm space-y-2">
              <div className="font-bold">What to do</div>

              <div className="whitespace-pre-wrap leading-relaxed">{taskText}</div>

              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  Type your finished work in the big box below, or write it on
                  paper and upload a photo.
                </li>
                {l.response_prompt && (
                  <li>
                    Then answer the question in the small box: "
                    {l.response_prompt}"
                  </li>
                )}
              </ol>
            </div>

            <PrintableResource resource={l.printable_resource} />

            {plannerFields.length > 0 && (
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm">
                <div className="font-bold mb-1">
                  {l.planner_title ? l.planner_title : "Your plan"}
                </div>

                {plannerFields.map((f) => (
                  <div key={f.key}>
                    <strong>{f.label}:</strong> {plan[f.key]}
                  </div>
                ))}
              </div>
            )}

            <div>
              <label className="text-sm font-bold">Your finished work</label>

              <textarea
                rows={8}
                value={response}
                onChange={(event) => setResponse(event.target.value)}
                placeholder="Type here… or leave empty and upload a photo of your paper work"
                className="mt-1 w-full rounded-lg border px-3 py-2 text-base bg-white"
                style={{ borderColor: "#D4C8A8" }}
                data-testid="response-text"
              />
            </div>

            {l.response_prompt && (
              <div>
                <label className="text-sm font-bold">
                  Your answer: {l.response_prompt}
                </label>

                <textarea
                  rows={2}
                  value={promptAnswer}
                  onChange={(event) => setPromptAnswer(event.target.value)}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-white"
                  style={{ borderColor: "#D4C8A8" }}
                />
              </div>
            )}

            <input
              ref={fileRef}
              type="file"
              accept="image/*,audio/*,video/*,application/pdf"
              onChange={(event) =>
                event.target.files[0] &&
                uploadFile(event.target.files[0], setFiles)
              }
              className="hidden"
              data-testid="file-input"
            />

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-xl border-2 border-dashed w-full p-5 flex flex-col items-center gap-1 hover:bg-stone-50 disabled:opacity-50"
              style={{ borderColor: "#D4C8A8" }}
              data-testid="upload-btn"
            >
              {uploading ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Upload size={20} />
              )}

              <span className="text-sm font-bold">
                {uploading
                  ? "Uploading…"
                  : "Add a photo, recording or PDF of your work"}
              </span>
            </button>

            {files.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {files.map((file) => (
                  <FilePreview key={file.id} file={file} />
                ))}
              </div>
            )}

            <div>
              <label className="text-sm font-bold">Reflect (optional)</label>

              <textarea
                rows={2}
                value={reflection}
                onChange={(event) => setReflection(event.target.value)}
                placeholder={
                  l.reflection_prompts?.[0] ||
                  l.reflection_prompt ||
                  "What did you learn? What was tricky?"
                }
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-white"
                style={{ borderColor: "#D4C8A8" }}
                data-testid="reflection-text"
              />
            </div>
          </div>
        );

      case "quiz":
        return (
          <QuestQuiz
            quiz={l.quiz}
            passMark={l.pass_mark || 0.9}
            assignmentId={aid}
            onResult={setQuizResult}
            shortAnswers={quizShortAnswers}
            onShortAnswers={setQuizShortAnswers}
          />
        );

      case "submit":
        return (
          <div className="space-y-3">
            <p className="text-sm">
              You have finished every stage. Hand in your work for a grown-up to
              review.
            </p>

            {hasHoardWords && (
              <div
                className="rounded-xl border p-4 text-sm space-y-2"
                style={{ backgroundColor: "#FBF3E4", borderColor: "#C77B5B" }}
                data-testid="word-hoard-reminder"
              >
                <div className="font-bold" style={{ color: "#1F3B2D" }}>
                  🐾 Don't forget your Word Hoard!
                </div>

                <p className="text-stone-700">
                  The spelling words from this lesson are waiting in your Word
                  Hoard. Spell each one right 10 times in a row to master it.
                </p>

                <button
                  type="button"
                  onClick={() => nav("/child/word-hoard")}
                  className="rounded-full px-5 py-2 text-sm font-bold"
                  style={{ backgroundColor: "#C77B5B", color: "#F5EFE0" }}
                  data-testid="go-word-hoard"
                >
                  Go to my Word Hoard
                </button>
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={needsHelp}
                onChange={(event) => setNeedsHelp(event.target.checked)}
                className="mt-1 h-5 w-5"
                data-testid="needs-help"
              />

              <span className="text-sm">
                I need help from a grown-up before I finish this.
              </span>
            </label>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={submit}
                disabled={submitting || submitted || !quizPassed}
                className="rounded-full px-6 py-3 text-sm font-bold hover:translate-y-[-1px] transition disabled:opacity-50 flex items-center gap-2"
                style={CONTINUE_STYLE}
                data-testid="submit-work"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : submitted ? (
                  <>
                    <CheckCircle2 size={14} />
                    Submitted
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    {needsHelp ? "Send for help" : "Submit for review"}
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={printLesson}
                className="rounded-full border px-4 py-3 text-sm font-bold flex items-center gap-2"
                style={{ borderColor: "#D4C8A8" }}
                data-testid="print-btn"
              >
                <Printer size={14} />
                Print
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-5 animate-in relative" data-testid="child-lesson">
      <Leaf
        className="absolute right-0 top-0 opacity-40"
        size={60}
        color="#6B8A5B"
      />

      <button
        type="button"
        onClick={() => nav("/child")}
        className="text-sm font-bold flex items-center gap-1"
        style={{ color: "#4A5D3A" }}
        data-testid="back-home"
      >
        <ArrowLeft size={14} />
        Back
      </button>

      <header className="paper-card p-6 relative overflow-hidden">
        <QuestBanner lesson={l} />
        <button type="button" onClick={printLesson} className="mt-2 rounded-full border px-4 py-2 text-sm font-bold" style={{ borderColor: "#D4C8A8" }} data-testid="print-lesson-top"><Printer size={14} className="inline mr-1" /> Print student workbook</button>

        <div className="text-xs font-mono text-stone-500">
          {l.stage} · {l.learning_area}
        </div>

        <h1
          className="font-display text-3xl font-bold mt-1"
          style={{ color: "#1F3B2D" }}
        >
          {l.title}
        </h1>

        <div
          className={`mt-4 rounded-xl border px-4 py-2 text-sm ${supportBanner.cls}`}
          data-testid="support-banner"
        >
          {supportBanner.text}
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-xs font-mono text-stone-500">
            <span>
              Quest progress: {completed} of {stages.length} stages
            </span>
            <span>{percent}%</span>
          </div>

          <div className="h-2 rounded-full bg-stone-200 mt-1 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${percent}%`, backgroundColor: "#4A5D3A" }}
            />
          </div>
        </div>
      </header>

      <div className="space-y-3">
        {stages.map((stage, i) => {
          const unlocked = i <= current;
          const finished = stageDone(stage.key);
          const isOpen = open === i && unlocked;
          const canSkip =
            isOpen &&
            !finished &&
            ["sort", "watch", "words"].includes(stage.key);

          return (
            <section
              key={stage.key}
              className="paper-card overflow-hidden"
              data-testid={`stage-${stage.key}`}
            >
              <button
                type="button"
                disabled={!unlocked}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center gap-3 p-4 text-left disabled:opacity-60"
              >
                <span className="text-xl">{unlocked ? stage.icon : ""}</span>

                {!unlocked && <Lock size={16} className="text-stone-400" />}

                <span className="flex-1">
                  <span className="block text-[10px] font-mono uppercase text-stone-500">
                    Stage {i + 1} of {stages.length}
                  </span>

                  <span
                    className="font-display text-lg font-semibold"
                    style={{ color: "#1F3B2D" }}
                  >
                    {stage.title}
                  </span>
                </span>

                {finished && (
                  <CheckCircle2 size={20} className="text-green-700" />
                )}
              </button>

              {isOpen && (
                <div className="px-5 pb-5">
                  {renderBody(stage.key)}

                  {stage.key !== "accept" &&
                    stage.key !== "submit" &&
                    i < stages.length - 1 && (
                      <div className="mt-5 flex items-center gap-3 flex-wrap">
                        <button
                          type="button"
                          disabled={!finished}
                          onClick={() => advance(i)}
                          className="rounded-full px-6 py-2.5 text-sm font-bold disabled:opacity-40"
                          style={CONTINUE_STYLE}
                          data-testid={`continue-${stage.key}`}
                        >
                          {finished
                            ? "Continue to the next stage →"
                            : "Finish this stage to continue"}
                        </button>

                        {canSkip && (
                          <button
                            type="button"
                            onClick={() => skipStage(stage.key)}
                            className="text-xs underline text-stone-500"
                          >
                            Grown-up: skip this activity
                          </button>
                        )}
                      </div>
                    )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {a.status === "needs_revision" && a.submissions?.[0]?.response_text && (
        <details className="paper-card p-4 text-sm" data-testid="previous-submitted-work">
          <summary className="cursor-pointer font-semibold">See the answers I already handed in</summary>
          <p className="mt-2">Your previous answers are still recorded, even if they could not be restored into the boxes automatically.</p>
          <pre className="mt-2 whitespace-pre-wrap break-words font-sans">{a.submissions[0].response_text}</pre>
          {a.submissions[0].parent_feedback && <p className="mt-2"><strong>Parent feedback:</strong> {a.submissions[0].parent_feedback}</p>}
        </details>
      )}
      {submitted && hasHoardWords && (
        <div
          className="paper-card p-5 flex items-center gap-4 flex-wrap"
          style={{ backgroundColor: "#FBF3E4", borderColor: "#C77B5B" }}
          data-testid="word-hoard-reminder-done"
        >
          <div className="flex-1 min-w-[220px]">
            <div
              className="font-display text-lg font-bold"
              style={{ color: "#1F3B2D" }}
            >
              🐾 Time to check your Word Hoard
            </div>

            <p className="text-sm text-stone-700 mt-1">
              Your new spelling words are in there. Practise them until you can
              spell each one right 10 times in a row.
            </p>
          </div>

          <button
            type="button"
            onClick={() => nav("/child/word-hoard")}
            className="rounded-full px-5 py-2.5 text-sm font-bold"
            style={{ backgroundColor: "#C77B5B", color: "#F5EFE0" }}
          >
            Go to my Word Hoard
          </button>
        </div>
      )}

      {l.follow_up_challenges?.length > 0 && (
        <div
          className="paper-card p-6"
          style={{ backgroundColor: "#F0F4E8", borderColor: "#94A47F" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <Target size={18} style={{ color: "#4A5D3A" }} />

            <h2
              className="font-display text-xl font-bold"
              style={{ color: "#1F3B2D" }}
            >
              Prove it! Follow-up challenges
            </h2>
          </div>

          <p className="text-sm text-stone-700 mb-4">
            {submitted
              ? "Pick a challenge to really show you've got it. Submit any and earn bonus pet XP."
              : "Submit your main task first — then these unlock."}
          </p>

          <div className="grid md:grid-cols-3 gap-3">
            {l.follow_up_challenges.map((challenge, index) => (
              <button
                key={index}
                type="button"
                onClick={() => submitted && setActiveChallenge(challenge)}
                disabled={!submitted}
                className={`rounded-xl p-4 text-left border bg-white transition ${
                  submitted
                    ? "hover:border-moss hover:translate-y-[-2px] cursor-pointer"
                    : "opacity-60 cursor-not-allowed"
                }`}
                style={{
                  borderColor: submitted ? "#4A5D3A" : "#D4C8A8"
                }}
                data-testid={`challenge-${index}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {!submitted && <Lock size={10} className="text-stone-400" />}

                  <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                    {challenge.type?.replace(/_/g, " ")}
                  </span>

                  <span
                    className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      challenge.difficulty === "stretch"
                        ? "bg-rose-100 text-rose-900"
                        : challenge.difficulty === "medium"
                          ? "bg-amber-100 text-amber-900"
                          : "bg-emerald-100 text-emerald-900"
                    }`}
                  >
                    {challenge.difficulty}
                  </span>
                </div>

                <div
                  className="font-display font-bold text-sm"
                  style={{ color: "#1F3B2D" }}
                >
                  {challenge.title}
                </div>

                <div className="text-xs text-stone-600 mt-1">
                  {challenge.description}
                </div>

                <div className="text-[10px] mt-2 font-mono text-stone-500">
                  Show: {challenge.evidence_type}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeChallenge && (
        <div
          className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setActiveChallenge(null)}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-lg paper-card p-6"
            data-testid="challenge-modal"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-script text-xl" style={{ color: "#C77B5B" }}>
                Follow-up challenge
              </div>

              <button
                type="button"
                onClick={() => setActiveChallenge(null)}
                aria-label="Close challenge"
              >
                <ArrowLeft size={18} />
              </button>
            </div>

            <h3
              className="font-display text-2xl font-bold"
              style={{ color: "#1F3B2D" }}
            >
              {activeChallenge.title}
            </h3>

            <p className="mt-2 text-sm">{activeChallenge.description}</p>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
                What you did
              </label>

              <textarea
                rows={4}
                value={challengeResponse}
                onChange={(event) => setChallengeResponse(event.target.value)}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-white"
                style={{ borderColor: "#D4C8A8" }}
                data-testid="challenge-response"
              />
            </div>

            <input
              ref={chFileRef}
              type="file"
              accept="image/*,audio/*,video/*,application/pdf"
              onChange={(event) =>
                event.target.files[0] &&
                uploadFile(event.target.files[0], setChallengeFiles)
              }
              className="hidden"
            />

            <button
              type="button"
              onClick={() => chFileRef.current?.click()}
              className="mt-3 w-full rounded-xl border-2 border-dashed p-3 text-sm font-bold flex items-center justify-center gap-2"
              style={{ borderColor: "#D4C8A8" }}
              data-testid="challenge-upload"
            >
              <Upload size={14} />
              Add evidence ({activeChallenge.evidence_type})
            </button>

            {challengeFiles.length > 0 && (
              <div className="mt-2 grid grid-cols-4 gap-2">
                {challengeFiles.map((file) => (
                  <FilePreview key={file.id} file={file} />
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={submitChallenge}
              className="mt-4 w-full rounded-full py-3 text-sm font-bold"
              style={{ backgroundColor: "#C77B5B", color: "#F5EFE0" }}
              data-testid="submit-challenge"
            >
              Submit challenge
            </button>
          </div>
        </div>
      )}

      <PetCompanion lessonId={l.id} stageKey={stages[open >= 0 ? open : current]?.key} stageTitle={stages[open >= 0 ? open : current]?.title} />
    </div>
  );
}

const FilePreview = ({ file }) => {
  const Icon = file.content_type?.startsWith("image/")
    ? ImgIcon
    : file.content_type?.startsWith("audio/")
      ? Mic
      : file.content_type?.startsWith("video/")
        ? Film
        : FileText;

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{ borderColor: "#D4C8A8" }}
    >
      {file.content_type?.startsWith("image/") ? (
        <img
          src={fileUrl(file.id)}
          alt={file.original_filename}
          className="w-full h-20 object-cover"
        />
      ) : (
        <div className="h-20 bg-stone-50 grid place-items-center text-stone-500">
          <Icon size={20} />
        </div>
      )}

      <div className="p-1 text-[10px] truncate">{file.original_filename}</div>
    </div>
  );
};

